import { ReactNode } from 'react'
import { TFunctionResult } from 'i18next'
import { Link } from 'react-router-dom'
import { omit } from 'ramda'
import styled, { css } from 'styled-components'
import { CssUnit } from '@/types'
import { toUnit } from '@/utils/styles'
import { colors, fontWeights } from '@/styles'
import { LoadingSpinner } from '@/components/loading-circle'

const LoadingWrapper = styled.div`
  height: 1px;
  display: flex;
  align-items: center;
  overflow-y: visible;
`

type Theme = 'primary' | 'secondary' | 'delete' | 'success' | 'text'
type Size = 'small' | 'medium'
type Shape = 'default' | 'round'
type Space = 'none' | 'fit' | 'normal'

type ButtonProps = {
  id?: string
  onClick?: (() => void) | ((e: React.MouseEvent<HTMLElement>) => void)
  to?: string
  href?: string
  disabled?: boolean
  type?: string
  target?: string
  rel?: string
  theme?: Theme
  size?: Size
  shape?: Shape
  space?: Space
  width?: CssUnit
  loading?: boolean
  tabIndex?: number
  minWidth?: CssUnit
  minHeight?: CssUnit
  padding?: CssUnit
  children?: ReactNode | TFunctionResult
}

const primary = css`
  color: ${colors.white};
  background-color: ${colors.blue16};
  box-shadow:
    0px 2px 4px rgba(19, 43, 62, 0.25),
    0px 6px 6px 2px rgba(19, 43, 62, 0.1);
  &:hover {
    background-color: ${colors.blue24};
    box-shadow:
      0px 6px 8px -2px rgba(29, 66, 94, 0.4),
      0px 12px 16px rgba(32, 121, 193, 0.15);
  }
  &:active {
    background-color: ${colors.blue08} !important;
    color: #9ca3a5;
    box-shadow: none !important;
  }
`

const secondary = css`
  color: ${colors.blue16};
  background-color: ${colors.white};
  border: 1.5px solid ${colors.grey88};
  &:hover {
    background-color: ${colors.grey96};
  }
  &:active {
    background-color: ${colors.grey88} !important;
  }
`

const del = css`
  color: ${colors.red64};
  background-color: ${colors.red96};
  border: 1.5px solid ${colors.red88};
  &:hover {
    background-color: ${colors.red88};
  }
  &:active {
    color: ${colors.red56} !important;
    background-color: ${colors.red80} !important;
  }
`

const text = css`
  color: ${colors.link};
  background-color: transparent;
  border: none;
  font-weight: 600;
  &:hover {
    background-color: transparent;
  }
  &:active {
    color: ${colors.link} !important;
    background-color: transparent;
  }
`

const success = css`
  color: ${colors.white};
  background: #00cca3;
  box-shadow: none;
  &:hover {
    background-color: ${colors.green48};
    box-shadow:
      0px 6px 8px -2px rgba(0, 245, 195, 0.4),
      0px 12px 16px rgba(0, 163, 130, 0.15);
  }
  &:active {
    background-color: ${colors.green32} !important;
    color: #9adacd;
    box-shadow: none !important;
  }
`

const disabled = css`
  cursor: not-allowed;
  background-color: ${colors.backgroundDisabled};
  color: ${colors.foregroundDisabled};
  border: 0;
`

const disabledText = css`
  cursor: not-allowed;
  background-color: transparent;
  border: none;
  color: ${colors.grey80};
`

const small = css`
  min-width: 96px;
  font-weight: ${fontWeights.bold};
  font-size: 12px;
  line-height: 16px;
  padding: 12px;
  border-radius: 6px;
  gap: 0 4px;
  min-height: 40px;
`

const medium = css`
  min-width: 160px;
  font-weight: ${fontWeights.bold};
  font-size: 14px;
  line-height: 16px;
  padding: 16px;
  border-radius: 6px;
  gap: 0 8px;
  min-height: 52px;
`

const round = css`
  border-radius: 50%;
  min-width: unset;
  min-height: unset;
`

const none = css`
  padding: 0;
  height: unset;
  width: unset;
  min-width: unset;
  min-height: unset;
  justify-content: flex-start;
`

const fit = css`
  padding: 0;
  height: 100%;
  width: 100%;
  min-width: unset;
  min-height: unset;
  justify-content: flex-start;
`

const getVariantStyles = (props: ButtonProps) => {
  if (props.theme === 'success') return success
  if (props.theme === 'delete') return del
  if (props.theme === 'secondary') return secondary
  if (props.theme === 'text') return text
  return primary
}

const getSizeStyles = (props: ButtonProps) => {
  if (props.size === 'small') return small
  return medium
}

const getShapeStyles = (props: ButtonProps) => {
  if (props.shape === 'round') return round
  return ''
}

const getSpaceStyles = (props: ButtonProps) => {
  if (props.space === 'none') return none
  if (props.space === 'fit') return fit
  return ''
}

const getDisabledStyles = (props: ButtonProps) => {
  if (props.theme === 'text') return disabledText
  return disabled
}

const BaseButton = styled.button<ButtonProps>`
  transition:
    color 300ms ease-in-out 0s,
    background-color 300ms ease-in-out 0s,
    box-shadow 300ms ease-in-out 0s;
  border: none;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: ${props => (props.width ? toUnit(props.width) : 'unset')};
  min-width: ${props => (props.minWidth ? `${toUnit(props.minWidth)} !important` : 'unset')};
  min-height: ${props => (props.minHeight ? `${toUnit(props.minHeight)} !important` : 'unset')};
  padding: ${props => (props.padding ? `${toUnit(props.padding)} !important` : 'unset')};

  &:disabled,
  &[aria-disabled='true'] {
    cursor: not-allowed;
    user-select: none;
    pointer-events: ${props => (props.loading ? 'none' : 'auto')};
    ${props => (props.loading ? getVariantStyles(props) : getDisabledStyles(props))};
  }
  &:not(:disabled):not([aria-disabled='true']) {
    ${props => getVariantStyles(props)};
  }

  ${props => getSizeStyles(props)};
  ${props => getShapeStyles(props)};
  ${props => getSpaceStyles(props)};

  svg {
    pointer-events: none;
  }
`

type Polymorphic = { as?: React.ComponentProps<typeof BaseButton>['as'] }

const Button: React.FC<ButtonProps & Polymorphic> = props => {
  const { loading, children, to, href, disabled, as } = props

  const getComponent = () => {
    if (!disabled) {
      if (!!to) return Link
      if (!!href) return 'a'
    }
    return 'button'
  }

  const getButtonProps = () => {
    if (!disabled) {
      if (!!to || !!href) return omit(['onClick', 'type'], props)
    }
    return omit(['to', 'href'], props)
  }

  const component = getComponent()
  const buttonProps = getButtonProps()

  return (
    <BaseButton as={as ?? component} {...buttonProps}>
      {!loading ? (
        children
      ) : (
        <LoadingWrapper>
          <LoadingSpinner />
        </LoadingWrapper>
      )}
    </BaseButton>
  )
}

Button.defaultProps = {
  type: 'button',
  theme: 'primary',
  shape: 'default',
  space: 'normal',
  size: 'medium'
}

export default Button
